import { Typography, Box, Tooltip, Menu, MenuItem } from "@mui/material";
import { Draggable } from "react-beautiful-dnd";
import { useState, useEffect, useMemo } from "react";
import { Horse } from "../HorseManagement/types";
import HorseIcon from "../HorseManagement/HorseIcon";

interface ProColorMapping {
  PROS: Record<number, [string, string]>;
  PLAYERS: Record<number, number>;
  MAPPING: Record<number, string>;
}

interface PlayerItemMinimalProps {
  id: string;
  managerMode: number;
  index: number;
  firstName: string;
  lastName: string;
  rating: number;
  chukker: string;
  team: string;
  isBench: boolean;
  proColorMapping?: ProColorMapping;
  betaMode: boolean;
  sx?: any;
  devPort?: string;
  clubId?: number;
  eventId?: number;
  eventType?: "games" | "lessons" | "tournaments";
  playerId?: number;
  playerIndex?: number;
  horseId?: string; // Horse ID stored in player data object
  updateHorseAssignment?: (playerId: string, chukker: string, horseId: string | null) => void;
  chukkerList?: Record<string, Record<string, Record<string, any[]>>>; // Full chukker list to count assignments
  allHorseAssignments?: Record<string, any[]>; // { playerId: assignments[] } - preloaded to avoid API calls
  allPlayerHorses?: Record<string, any[]>; // { playerId: horses[] } - preloaded to avoid API calls
  allClubHorses?: any[]; // All club horses - preloaded to avoid API calls
}

function PlayerItemMinimal(props: PlayerItemMinimalProps) {
  const [playerHorses, setPlayerHorses] = useState<Horse[]>([]);
  // Store initial assignments with chukkers_assigned counts
  const [initialAssignments, setInitialAssignments] = useState<Record<string, number>>({}); // { horseId: totalChukkersAssigned }
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Use horseId directly from props
  const [selectedHorseId, setSelectedHorseId] = useState<string>(props.horseId || "");
  
  // Update selectedHorseId when props.horseId changes
  useEffect(() => {
    setSelectedHorseId(props.horseId || "");
  }, [props.horseId]);

  // Get pro color for left border indicator (assigned pro)
  const proColor = props.proColorMapping &&
    props.proColorMapping["PROS"] &&
    props.proColorMapping["PROS"][props.index]
      ? props.proColorMapping["PROS"][props.index][0]
      : null;

  // Get requested pro information
  const requestedProId = props.proColorMapping &&
    props.proColorMapping["PLAYERS"] &&
    props.proColorMapping["PLAYERS"][props.index]
      ? props.proColorMapping["PLAYERS"][props.index]
      : null;

  const requestedProInfo = requestedProId &&
    props.proColorMapping &&
    props.proColorMapping["PROS"] &&
    props.proColorMapping["PROS"][requestedProId]
      ? {
          color: props.proColorMapping["PROS"][requestedProId][0],
          image: props.proColorMapping["PROS"][requestedProId][1],
          name: props.proColorMapping["MAPPING"] && props.proColorMapping["MAPPING"][requestedProId]
            ? props.proColorMapping["MAPPING"][requestedProId]
            : null,
        }
      : null;

  // Load player's horses from props (preloaded) - only use preloaded data, no API calls
  useEffect(() => {
    if (props.isBench || !props.playerId) {
      setPlayerHorses([]);
      setInitialAssignments({});
      return;
    }
    
    const playerIdStr = String(props.playerId);
    
    // Only use preloaded data - don't make API calls
    if (props.allPlayerHorses && props.allHorseAssignments && props.allClubHorses) {
      const ownHorses = props.allPlayerHorses[playerIdStr] || [];
      const assignments = props.allHorseAssignments[playerIdStr] || [];
      const clubHorses = props.allClubHorses || [];
      
      // Extract horse IDs and their total chukkers_assigned from assignments
      const assignedHorseIds = new Set<string>();
      const assignmentCounts: Record<string, number> = {};
      assignments.forEach((assignment: any) => {
        if (assignment.horse_id) {
          const horseId = String(assignment.horse_id);
          assignedHorseIds.add(horseId);
          assignmentCounts[horseId] = (assignmentCounts[horseId] || 0) + (assignment.chukkers_assigned || 0);
        }
      });
      setInitialAssignments(assignmentCounts);
      
      // Combine all horses: player's own + horses already assigned to them
      const allHorseIds = new Set<string>();
      const allHorses: Horse[] = [];
      
      // Add player's own horses
      ownHorses.forEach((horse: Horse) => {
        allHorseIds.add(horse.id);
        allHorses.push(horse);
      });
      
      // Add horses that are assigned to this player (from club horses)
      assignedHorseIds.forEach((horseId: string) => {
        if (!allHorseIds.has(horseId)) {
          const assignedHorse = clubHorses.find((h: Horse) => h.id === horseId);
          if (assignedHorse) {
            allHorseIds.add(horseId);
            allHorses.push(assignedHorse);
          }
        }
      });
      
      setPlayerHorses(allHorses);
    } else {
      // If preloaded data not available yet, just wait (don't fetch)
      setPlayerHorses([]);
      setInitialAssignments({});
    }
  }, [props.allPlayerHorses, props.allHorseAssignments, props.allClubHorses, props.playerId, props.isBench]);

  // Calculate remaining chukkers per horse (initial assignments minus current assignments across all chukkers)
  const horseChukkerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    
    // Initialize with initial assignments (total chukkers assigned from signup)
    playerHorses.forEach((horse) => {
      counts[horse.id] = initialAssignments[horse.id] || 0;
    });
    
    // Count how many times each horse is currently assigned across all chukkers for this player
    if (props.chukkerList && props.playerId) {
      const playerIdStr = String(props.playerId);
      const chukkerList = props.chukkerList;
      Object.keys(chukkerList).forEach((chukkerId) => {
        if (chukkerId !== "-1") {
          Object.keys(chukkerList[chukkerId]).forEach((teamId) => {
            const team = chukkerList[chukkerId][teamId];
            if (team) {
              Object.keys(team).forEach((playerKey) => {
                const playerData = team[playerKey];
                // Check if this is the same player
                if (playerData && Array.isArray(playerData) && playerData[3] && String(playerData[3]) === playerIdStr) {
                  // Get horse ID from index 11 (assigned_horse) or fall back to horseId property
                  const horseId = (playerData.length > 11 && playerData[11] !== null && playerData[11] !== undefined) 
                    ? String(playerData[11]) 
                    : (playerData as any).horseId;
                  if (horseId && counts[horseId] !== undefined) {
                    counts[horseId] = Math.max(0, counts[horseId] - 1);
                  }
                }
              });
            }
          });
        }
      });
    }
    
    return counts;
  }, [playerHorses, initialAssignments, props.chukkerList, props.playerId]);

  // Handle horse selection - update state instead of calling API
  const handleHorseChange = (horseId: string) => {
    setSelectedHorseId(horseId);
    setAnchorEl(null); // Close menu
    
    if (!props.playerId || !props.updateHorseAssignment) {
      return;
    }

    const chukkerNum = parseInt(props.chukker);
    if (chukkerNum <= 0) return; // Don't assign for bench

    // Update state via callback
    props.updateHorseAssignment(String(props.playerId), props.chukker, horseId || null);
  };

  const handleBadgeClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent drag
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Get selected horse for display
  // For players, we might have a horseId but not have loaded the full horse object
  // So we create a minimal horse object if we have the ID but not the full object
  const selectedHorse = selectedHorseId 
    ? (playerHorses.find((h) => h.id === selectedHorseId) || 
       (selectedHorseId ? { id: selectedHorseId, name: "Horse", color: "Brown" } : null))
    : null;

  // Create unique draggableId by combining chukker, team, and playerId
  const uniqueDraggableId = `${props.chukker}____${props.team}____${props.id}`;
  // Use playerIndex if provided, otherwise fall back to a computed index
  // The index should be unique within each droppable (team cell)
  const draggableIndex = props.playerIndex !== undefined ? props.playerIndex : parseInt(props.id);

  return (
    <Draggable
      draggableId={uniqueDraggableId}
      isDragDisabled={props.managerMode !== -1}
      index={draggableIndex}
    >
      {(provided: any, snapshot: any) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-testid={"player-" + props.id}
          sx={{
            border: "1px solid",
            borderColor: snapshot.isDragging ? "primary.main" : "grey.300",
            borderLeft: proColor ? `3px solid ${proColor}` : "1px solid",
            borderLeftColor: proColor || (snapshot.isDragging ? "primary.main" : "grey.300"),
            borderRadius: 1,
            p: "clamp(0.4rem, 1.2vh, 0.8rem)",
            mb: props.isBench ? 0 : 0.5,
            height: props.isBench ? "calc(100% - 1px)" : "calc(25% - 6px)",
            minHeight: props.isBench ? "calc(100% - 1px)" : "calc(25% - 6px)",
            width: props.isBench ? "auto" : "100%",
            backgroundColor: "white",
            cursor: props.managerMode === -1 ? "grab" : "default",
            opacity: snapshot.isDragging ? 0.8 : 1,
            transition: "all 0.2s ease",
            boxShadow: snapshot.isDragging ? 2 : "0 1px 2px rgba(0,0,0,0.05)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "stretch",
            gap: "clamp(0.1rem, 0.5vh, 0.3rem)",
            "&:hover": props.managerMode === -1
              ? {
                  borderColor: "primary.main",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  transform: "translateY(-1px)",
                }
              : {},
            "&:active": {
              cursor: props.managerMode === -1 ? "grabbing" : "default",
            },
            ...props.sx,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minHeight: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: "clamp(0.75rem, 3vh, 1rem)",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.3,
              }}
            >
              {props.firstName} {props.lastName}
            </Typography>
            {requestedProInfo && (
              <Tooltip title={requestedProInfo.name || "Pro Request"} arrow>
                <Box
                  sx={{
                    width: "clamp(16px, 1.5vh, 20px)",
                    height: "clamp(16px, 1.5vh, 20px)",
                    borderRadius: "50%",
                    border: "1.5px solid",
                    borderColor: requestedProInfo.color || "grey.400",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    src={requestedProInfo.image}
                    alt="Pro"
                    sx={{
                      width: "clamp(12px, 1.2vh, 16px)",
                      height: "clamp(12px, 1.2vh, 16px)",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Tooltip>
            )}
            {!props.isBench && (
              <>
                {selectedHorse ? (
                  <Tooltip title={selectedHorse.name} arrow>
                    <Box
                      onClick={props.managerMode === -1 ? handleBadgeClick : undefined}
                      sx={{
                        width: "clamp(16px, 1.5vh, 20px)",
                        height: "clamp(16px, 1.5vh, 20px)",
                        borderRadius: "50%",
                        border: "1.5px solid",
                        borderColor: "grey.400",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: props.managerMode === -1 ? "pointer" : "default",
                        overflow: "hidden",
                        "&:hover": props.managerMode === -1 ? {
                          borderColor: "primary.main",
                          transform: "scale(1.1)",
                        } : {},
                        transition: "all 0.2s ease",
                      }}
                    >
                      <HorseIcon 
                        color={selectedHorse.color} 
                        size={14}
                      />
                    </Box>
                  </Tooltip>
                ) : props.managerMode === -1 && playerHorses.length > 0 ? (
                  <Tooltip title="Click to assign horse" arrow>
                    <Box
                      onClick={handleBadgeClick}
                      sx={{
                        width: "clamp(16px, 1.5vh, 20px)",
                        height: "clamp(16px, 1.5vh, 20px)",
                        borderRadius: "50%",
                        border: "1.5px dashed",
                        borderColor: "grey.400",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "primary.main",
                          backgroundColor: "action.hover",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "clamp(0.5rem, 1vh, 0.7rem)",
                          color: "text.secondary",
                        }}
                      >
                        +
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : null}
              </>
            )}
          </Box>
          {(props.managerMode !== -1 || props.betaMode) && (
            <Typography
              variant="caption"
              sx={{
                fontSize: "clamp(0.65rem, 2.5vh, 0.85rem)",
                color: "text.secondary",
                display: "block",
                lineHeight: 1.2,
              }}
            >
              {props.rating}
            </Typography>
          )}
          {props.managerMode === -1 && !props.isBench && playerHorses.length > 0 && (
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
              PaperProps={{
                sx: {
                  maxHeight: 200,
                  mt: 0.5,
                },
              }}
            >
              <MenuItem 
                onClick={() => handleHorseChange("")}
                sx={{ fontSize: "0.7rem" }}
              >
                <em>No horse</em>
              </MenuItem>
              {playerHorses.map((horse) => {
                const remainingChukkers = horseChukkerCounts[horse.id] || 0;
                const isDisabled = remainingChukkers === 0;
                
                return (
                  <MenuItem 
                    key={horse.id}
                    onClick={() => handleHorseChange(horse.id)}
                    disabled={isDisabled}
                    sx={{
                      opacity: isDisabled ? 0.5 : 1,
                      color: isDisabled ? "text.disabled" : "text.primary",
                      fontSize: "0.7rem",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <HorseIcon color={horse.color} size={20} />
                        <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                          {horse.name}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: "0.65rem", 
                          color: isDisabled ? "text.disabled" : "text.secondary",
                          fontWeight: 500,
                        }}
                      >
                        ({remainingChukkers})
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>
          )}
        </Box>
      )}
    </Draggable>
  );
}

export default PlayerItemMinimal;

